import React, { useEffect, useState, useCallback, useContext } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../../config/axios'
import styled from 'styled-components'
import SectionContext from '../../../../../contexts/SectionContext'
import UserContext from '../../../../../contexts/UserContext'
import lookup from '../../../../../assets/equipment/Tables/lookup.svg'
import trash from '../../../../../assets/equipment/Tables/trash.svg'
import pencil from '../../../../../assets/equipment/Tables/pencil.svg'
import { EquipmentOutletType } from '../../index'
import ViewEquipmentModal from '../ViewEquipmentModal'
import { FeedbackContext } from '../../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../../components/ModalConfirmation'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/equipment`

interface BackendMachineListItem {
  id: number
  name: string
  model: string
  costPerHour: number
  lastMaintenanceDate?: string
  nextMaintenanceDate?: string
}

interface MachineData {
  id: number
  machineName: string
  model: string
  costPerHour: string
  lastMaintenance: string
  nextMaintenance: string
}

const MachinesTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
`

const MachinesTableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
`

const EquipmentTable: React.FC = () => {
  const { refresh, search, Styled } = useOutletContext<EquipmentOutletType>()
  const {
    GlobalTable,
    HeaderLine,
    Th,
    Tr,
    Td,
    TdButtons,
    DivInTd,
    InfoMessage,
  } = Styled
  const navigate = useNavigate()

  const [machines, setMachines] = useState<MachineData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showViewModal, setShowViewModal] = useState<boolean>(false)
  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(
    null,
  )
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [machineToDelete, setMachineToDelete] = useState<number | null>(null)

  const { permissions } = useContext(SectionContext)
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)

  const formatDate = useCallback((dateString?: string): string => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) {
        const [year, month, day] = dateString.split('-').map(Number)
        if (year && month && day) {
          const parsedDate = new Date(year, month - 1, day)
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString('pt-BR')
          }
        }
        return dateString
      }
      return date.toLocaleDateString('pt-BR')
    } catch (e) {
      console.warn('Could not parse date string:', dateString, e)
      return dateString
    }
  }, [])

  const formatCurrency = useCallback((value: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue) || numValue === null || numValue === undefined) {
      return 'R$ N/A'
    }
    return numValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    })
  }, [])
  const canDeleteEquipment = useCallback((): boolean => {
    // Allow admins to delete
    if (user?.role === 'ADMIN_GLOBAL' || user?.adminUser) return true

    const perms: string[] = Array.isArray(permissions) ? permissions : []
    // Check several possible permission naming conventions to be permissive
    const allowedKeys = ['equipment.delete', 'delete-equipment', 'delete_equipment', 'equipment']
    return allowedKeys.some((k) => perms.includes(k))
  }, [permissions, user])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    const token = localStorage.getItem('token')
    if (!token) {
      setError('Sessão expirada. Por favor, faça login novamente.')
      setLoading(false)
      navigate('/login')
      return
    }
    
    
    try {
      const response = await axios.get<BackendMachineListItem[]>(
        `${API_BASE_URL}/equipment`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      let fetchedMachines: MachineData[] = response.data.map((item) => ({
        id: item.id,
        machineName: item.name,
        model: item.model,
        costPerHour: formatCurrency(item.costPerHour),
        lastMaintenance: formatDate(item.lastMaintenanceDate),
        nextMaintenance: formatDate(item.nextMaintenanceDate),
      }))

      // Ordena as máquinas pelo ID em ordem decrescente (mais recentes primeiro)
      fetchedMachines.sort((a, b) => b.id - a.id)

      if (search) {
        fetchedMachines = fetchedMachines.filter(
          (item) =>
            item.machineName.toLowerCase().includes(search.toLowerCase()) ||
            item.model.toLowerCase().includes(search.toLowerCase()),
        )
      }
      setMachines(fetchedMachines)
    } catch (err) {
      console.error('Erro ao buscar dados das máquinas:', err)
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Não autorizado. Por favor, faça login novamente.')
        navigate('/login')
      } else {
        setError('Não foi possível carregar os equipamentos. Tente novamente.')
      }
    } finally {
      setLoading(false)
    }
  }, [search, refresh, navigate, formatDate, formatCurrency])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleViewDetails = (machineId: number) => {
    setSelectedMachineId(machineId)
    setShowViewModal(true)
  }

  const handleCloseViewModal = () => {
    setShowViewModal(false)
    setSelectedMachineId(null)
  }

  const handleEdit = (machineId: number) => {
    navigate(`/panel/equipment/edit/${machineId}`)
  }

  const handleDeleteClick = (machineId: number) => {
    if (!canDeleteEquipment()) {
      setFeedback({
        isOpen: true,
        message: 'Você não tem permissão para deletar equipamentos. Apenas usuários autorizados podem realizar esta ação.',
        statusOk: false,
      })
      return
    }
    setMachineToDelete(machineId)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (machineToDelete === null) return
    const machineId = machineToDelete
    setShowDeleteModal(false)
    setMachineToDelete(null)

    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({
        isOpen: true,
        message: 'Sessão expirada. Por favor, faça login novamente.',
        statusOk: false,
      })
      navigate('/login')
      return
    }

    try {
      setLoading(true)
      await axios.delete(`${API_BASE_URL}/equipment/${machineId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setMachines((prevMachines) =>
        prevMachines.filter((machine) => machine.id !== machineId),
      )
      setFeedback({
        isOpen: true,
        message: 'Equipamento excluído com sucesso!',
        statusOk: true,
      })
    } catch (err) {
      console.error(`Erro ao excluir máquina ${machineId}:`, err)
      let errorMessage = 'Falha ao excluir o equipamento. Tente novamente.'
      if (axios.isAxiosError(err)) {
        const backendMessage = err.response?.data?.message || ''
        if (
          backendMessage.includes('constraint') ||
          backendMessage.includes('foreign key') ||
          backendMessage.includes('fkey') ||
          backendMessage.includes('foreign-key')
        ) {
          errorMessage = 'O equipamento não pode ser excluído pois possui etapas de produção ou serviços vinculados a ele.'
        } else if (err.response?.status === 401) {
          setFeedback({
            isOpen: true,
            message: 'Não autorizado para excluir. Por favor, faça login novamente.',
            statusOk: false,
          })
          navigate('/login')
          return
        } else if (err.response?.status === 403) {
          errorMessage = 'Você não tem permissão para deletar este equipamento.'
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message
        }
      }
      setFeedback({
        isOpen: true,
        message: errorMessage,
        statusOk: false,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setMachineToDelete(null)
  }

  if (loading) {
    return <InfoMessage>Carregando equipamentos...</InfoMessage>
  }

  if (error) {
    return <InfoMessage style={{ color: 'red' }}>{error}</InfoMessage>
  }

  if (machines.length === 0) {
    return <InfoMessage>Nenhum equipamento encontrado.</InfoMessage>
  }

  const TableComponent: React.FC = () => {
    return (
      <>
        <MachinesTableWrapper>
          <MachinesTableScroll>
            <GlobalTable>
              <thead>
                <HeaderLine>
                  <Th>Máquina</Th>
                  <Th>Modelo</Th>
                  <Th>Custo por hora</Th>
                  <Th>Última manutenção</Th>
                  <Th>Próxima manutenção</Th>
                  <Th></Th>
                </HeaderLine>
              </thead>
              <tbody>
                {machines.map((machine) => (
                  <Tr key={machine.id}>
                    <Td data-label='Máquina'>{machine.machineName}</Td>
                    <Td data-label='Modelo'>{machine.model}</Td>
                    <Td data-label='Custo por hora'>{machine.costPerHour}</Td>
                    <Td data-label='Última manutenção'>
                      {machine.lastMaintenance}
                    </Td>
                    <Td data-label='Próxima manutenção'>
                      {machine.nextMaintenance}
                    </Td>
                    <TdButtons>
                      <DivInTd onClick={() => handleViewDetails(machine.id)}>
                        <img
                          src={lookup}
                          alt='Pesquisar'
                          title='Pesquisar'
                          style={{ cursor: 'pointer' }}
                        />
                      </DivInTd>
                      <DivInTd onClick={() => handleEdit(machine.id)}>
                        <img
                          src={pencil}
                          alt='Editar'
                          title='Editar'
                          style={{ cursor: 'pointer' }}
                        />
                      </DivInTd>
                      <DivInTd 
                        onClick={() => handleDeleteClick(machine.id)}
                        style={{ 
                          opacity: canDeleteEquipment() ? 1 : 0.5,
                          cursor: canDeleteEquipment() ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <img
                          src={trash}
                          alt='Excluir'
                          title={canDeleteEquipment() ? 'Excluir' : 'Sem permissão para excluir'}
                          style={{ 
                            cursor: canDeleteEquipment() ? 'pointer' : 'not-allowed',
                            filter: canDeleteEquipment() ? 'none' : 'grayscale(100%)'
                          }}
                        />
                      </DivInTd>
                    </TdButtons>
                  </Tr>
                ))}
              </tbody>
            </GlobalTable>
          </MachinesTableScroll>
        </MachinesTableWrapper>

        {showViewModal && selectedMachineId !== null && (
          <ViewEquipmentModal
            machineId={selectedMachineId}
            onClose={handleCloseViewModal}
          />
        )}

        <ConfirmationModal
          isOpen={showDeleteModal}
          title="Excluir Equipamento"
          message={`Tem certeza que deseja excluir a máquina com ID: ${machineToDelete}? Esta ação não pode ser desfeita.`}
          actions={[
            {
              label: 'Cancelar',
              onClick: handleCancelDelete,
              variant: 'outline'
            },
            {
              label: loading ? 'Excluindo...' : 'Excluir',
              onClick: handleConfirmDelete,
              variant: 'danger'
            }
          ]}
        />
      </>
    )
  }
  return <TableComponent />
}

export default EquipmentTable
